import type React from "react";

import { Checkbox, FormControl, InputLabel, ListItemText, MenuItem } from "@mui/material";
import type { SelectChangeEvent } from "@mui/material";
import { useTranslation } from "react-i18next";

import type { HealthReport } from "api/models/HealthReport";
import { StyledSelect } from "shared/atoms/MUI/MUISelect";
import { useLocalizedDate } from "utils/date";

interface ReportsDropdownProps {
  reportList?: HealthReport[];
  selected: number[];
  changeCallback: (selectedIds: number[]) => void;
}

export const ReportsDropdown: React.FC<ReportsDropdownProps> = ({ selected, reportList, changeCallback }) => {
  const { t } = useTranslation();
  const { format, parseISO } = useLocalizedDate();

  const handleChange = (event: SelectChangeEvent<unknown>) => {
    const { value } = event.target;
    const newSelected = typeof value === "string" ? value.split(",").map(Number) : (value as number[]);

    // Enforce at least one selection
    if (newSelected.length > 0) {
      changeCallback(newSelected);
    }
  };

  const renderValue = (value: unknown) => {
    const selectedIds = Array.isArray(value) ? (value as number[]) : [];
    switch (selectedIds.length) {
      case 0:
        return t("patients.health_reports.none");
      case 1: {
        const selection = reportList?.find(el => el.id === selectedIds[0]);
        if (selection) {
          return `${t("patients.health_reports.q_number", {
            number: selection.health_report_number,
          })} | ${format(parseISO(selection.created_at), "P")}`;
        }
        return "";
      }
      default:
        return t("patients.health_reports.selected", { number: selectedIds.length });
    }
  };

  return (
    <FormControl fullWidth data-testid="new-reports-dropdown">
      <InputLabel id="reports-select-label" shrink>
        {t("patients.health_reports.questionnaire", { number: "" })}
      </InputLabel>
      <StyledSelect
        labelId="reports-select-label"
        id="reports-select"
        multiple
        value={selected}
        onChange={handleChange}
        renderValue={renderValue}
        label={t("patients.health_reports.questionnaire", { number: "" })}
        disabled={!reportList?.length}
        data-testid="reports-select"
        size="small"
        displayEmpty
      >
        {reportList?.map(report => {
          const isSelected = selected.includes(report.id);
          const isDisabled = isSelected && selected.length === 1;

          return (
            <MenuItem
              key={report.id}
              value={report.id}
              disabled={isDisabled}
              data-testid={`report-option-${report.id}`}
              dense
              divider
              disableGutters
            >
              <Checkbox checked={isSelected} disabled={isDisabled} />
              <ListItemText
                primary={`${t("patients.health_reports.q_number", { number: report.health_report_number })} | ${format(
                  parseISO(report.created_at),
                  "P"
                )}`}
                sx={{ fontSize: 14 }}
              />
            </MenuItem>
          );
        })}
      </StyledSelect>
    </FormControl>
  );
};
